import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';

import { DateText } from '~/eds/src/components/DateText/DateText';
import { StatusMessage } from '~/eds/src/components/StatusMessage/StatusMessage';
import { TicketStatusType } from '~/enums/TicketStatusType';
import { useResolveUsers } from '~/hooks/useResolveUsers';
import Box from '~/ui/components/Box/Box';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import Text from '~/ui/components/Text/Text';
import { testIsUnassignedUser } from '~/utils/ticket';

import TicketDocumentNotice from './TicketDocumentNotice';

/**
 * Return coerced value of TicketStatusType.Completed if status === TicketStatusType.Approved.  Passthrough the status otherwise.
 */
export const coerceApprovalStatus = ({ status }) => {
  return status === TicketStatusType.Approved
    ? TicketStatusType.Completed
    : status;
};

function TicketDocumentNotices({
  // connected
  currentUser,
  ticket,
}) {
  const intl = useIntl();
  const { stages, phases, judgments, approvals, risks } = ticket;

  const reviewPhaseIds = stages.review?.phaseIds;
  const reviewApprovals = Object.values(approvals).filter((approval) =>
    reviewPhaseIds?.includes(approval.phaseId),
  );
  const isDocumentCompleted = reviewApprovals.every(
    (approval) => approval.status === TicketStatusType.Approved,
  );

  const userIds = reviewApprovals
    .filter((approval) => approval.userId)
    .map((approval) => approval.userId);

  const { users, isFetching } = useResolveUsers({
    userIds: userIds,
  });

  if (isFetching || !users) {
    return null;
  }

  if (isDocumentCompleted) {
    return (
      <Box mb={6}>
        <TicketDocumentNotice
          status={TicketStatusType.Approved}
          description={
            <Text variant="xs-dense">
              <FormattedMessage
                id="TicketDocumentNotices.noActionsNeeded"
                defaultMessage="No actions needed"
                description="Message indicating no actions are needed on the ticket document."
              />
            </Text>
          }
        />
      </Box>
    );
  }

  const unassignedCount = reviewApprovals.reduce((count, approval) => {
    const isApproved = approval.status === TicketStatusType.Approved;
    if (!isApproved && testIsUnassignedUser(approval, users)) {
      count++;
    }
    return count;
  }, 0);

  const invalidAssingments = reviewApprovals
    .filter((approval) => {
      const isApproved = approval.status === TicketStatusType.Approved;
      return (
        !isApproved && approval.userId && users[approval.userId]?.isDeleted
      );
    })
    .map((approval) => {
      return (
        <StatusMessage
          status="info"
          message={intl.formatMessage(
            {
              id: 'TicketDocumentNotices.userDeactivated',
              defaultMessage:
                'The user **{email}** has been deactivated. Re-assign their task and contact your Workflow Administrators to update the workflow.',
              description:
                'Message shown when a task is assigned to a deactivated user. Example Email = test@test.com',
            },
            {
              email: users[approval.userId]?.email,
            },
          )}
        />
      );
    });

  const unassignedTasks = !!unassignedCount && (
    <StatusMessage
      status="danger"
      message={intl.formatMessage(
        {
          id: 'TicketDocumentNotices.unassignedTasks',
          defaultMessage:
            'You have **{unassignedCount} {unassignedTasks, plural, one {unassigned task} other {unassigned tasks}}** that {unassignedCount, plural, one {needs} other {need}} to be assigned.',
          description:
            'Message for unassigned tasks, with pluralization for tasks and verb.',
        },
        {
          unassignedCount,
          unassignedTasks: unassignedCount,
        },
      )}
    />
  );

  const currentUserApprovals = reviewApprovals.filter(
    (approval) => approval.userId === currentUser.id,
  );
  const completedApprovals = [];
  let pendingCount = 0;
  currentUserApprovals.forEach((approval) => {
    if (approval.status === TicketStatusType.Pending) {
      ++pendingCount;
    } else {
      completedApprovals.push(approval);
    }
  });

  let pendingApprovals = null;
  if (pendingCount) {
    pendingApprovals = (
      <TicketDocumentNotice
        status={TicketStatusType.Pending}
        description={
          <Text variant="xs-dense">
            <FormattedMessage
              id="TicketDocumentNotices.pendingReviews"
              defaultMessage="You have <bold>{pendingCount} {review, plural, one {review} other {reviews}}</bold> pending completion."
              description="Message for pending reviews, with pluralization."
              values={{
                bold: (chunks) => <Text variant="xs-dense-bold">{chunks}</Text>,
                pendingCount,
                review: pendingCount,
              }}
            />
          </Text>
        }
      />
    );
  }

  return (
    <FlexLayout flexDirection="column" mb={6} space={2}>
      {completedApprovals.map((approval) => {
        const phaseName = phases[approval.phaseId].name;
        let description;
        if (approval.judgmentId) {
          const judgmentName = judgments[approval.judgmentId].name;
          if (judgmentName === 'General Approvers') {
            description = intl.formatMessage(
              {
                id: 'TicketDocumentNotices.youCoercedStatusPhaseName',
                defaultMessage: 'You {status} "{phaseName}"',
                description:
                  'Description of a completed approval action on a phase.',
              },
              {
                status: coerceApprovalStatus(approval),
                phaseName,
              },
            );
          } else {
            description = intl.formatMessage(
              {
                id: 'TicketDocumentNotices.youCoercedStatusPhaseJudgment',
                defaultMessage: 'You {status} "{phaseName} → {judgmentName}"',
                description:
                  'Description of a completed approval action on a phase and judgment.',
              },
              {
                status: coerceApprovalStatus(approval),
                phaseName,
                judgmentName,
              },
            );
          }
        } else {
          const riskName = risks[approval.riskId].name;
          description = intl.formatMessage(
            {
              id: 'TicketDocumentNotices.youCoercedStatusPhaseRisk',
              defaultMessage: 'You {status} "{phaseName} → {riskName}"',
              description:
                'Description of a completed approval action on a phase and risk.',
            },
            {
              status: coerceApprovalStatus(approval),
              phaseName,
              riskName,
            },
          );
        }

        const descriptionComponent = (
          <Text variant="xs-dense">
            {description}{' '}
            <Text variant="xs-dense-bold">
              <DateText
                date={
                  approval.lastModified
                    ? new Date(approval.lastModified)
                    : undefined
                }
                format="duration"
                enableTick
              />
            </Text>
            .
          </Text>
        );

        return (
          <TicketDocumentNotice
            key={`notice-${approval.id}`}
            status={approval.status}
            description={descriptionComponent}
            note={approval.comment}
          />
        );
      })}
      {unassignedTasks}
      {invalidAssingments}
      {pendingApprovals}
    </FlexLayout>
  );
}

function mapStateToProps({ currentUser, ticket }) {
  return { currentUser, ticket };
}

export default connect(mapStateToProps)(TicketDocumentNotices);
